import React, { useMemo, useState } from 'react';
import { Search, AlertTriangle, Clock, ArrowRight, Users, ArrowUpRight, ArrowDownRight, CheckCircle2, Download, Plus, FileText } from 'lucide-react';
import { MODULES, getModuleIcon } from '../../../content';
import { downloadCsv } from '../../../utils/csvExport';
import { formatRelativeTime } from '../../../utils/formatters';
import { GroupId } from '../../../config/systemConfig';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { EmptyState } from '../../../components/shared/EmptyState';
import { getAvatarGradient } from '../utils/avatarUtils';
import { needsAttention } from '../utils/studentUtils';
import { type Classroom, type TeacherAccount } from '../../../accounts';
import { getWeeklyActivityForLearner, getLearnerTrendDelta, type DayActivity } from '../../../utils/activityHelpers';
import { DjayAvatar } from '../../../mascot';
interface MyClassPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  classroomName?: string;
  onNavigate?: (route: string) => void;
}
type FilterType = 'all' | 'attention' | 'good' | 'inactive';
type SortType = 'name' | 'mastery' | 'activity';
export function MyClassPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  classroomName,
  onNavigate
}: MyClassPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [activeSort, setActiveSort] = useState<SortType>('name');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Enrich student data
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
      const trendDelta = getLearnerTrendDelta(student.data.familyMastery || {}, selectedGroup);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        weeklyActivity,
        daysPracticed,
        trendDelta,
        needsAttention: needsAttention(lowestMastery, practicedCount),
        isGood: avgMastery >= 70,
        isInactive: daysPracticed === 0
      };
    });
  }, [students, getModuleMastery, selectedGroup]);
  // Filter and sort
  const filteredAndSortedStudents = useMemo(() => {
    let result = enrichedStudents;
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(query));
    }
    // Filter
    switch (activeFilter) {
      case 'attention':
        result = result.filter(s => s.needsAttention);
        break;
      case 'good':
        result = result.filter(s => s.isGood);
        break;
      case 'inactive':
        result = result.filter(s => s.isInactive);
        break;
    }
    // Sort
    result.sort((a, b) => {
      switch (activeSort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'mastery':
          return b.avgMastery - a.avgMastery;
        case 'activity':
          return b.daysPracticed - a.daysPracticed;
        default:
          return 0;
      }
    });
    return result;
  }, [enrichedStudents, searchQuery, activeFilter, activeSort]);
  // Get counts for filter tabs
  const counts = useMemo(() => {
    return {
      all: enrichedStudents.length,
      attention: enrichedStudents.filter(s => s.needsAttention).length,
      good: enrichedStudents.filter(s => s.isGood).length,
      inactive: enrichedStudents.filter(s => s.isInactive).length
    };
  }, [enrichedStudents]);
  const getMasteryColorClass = (mastery: number) => {
    if (mastery === 0) return 'text-slate-400';
    if (mastery >= 70) return 'text-emerald-600';
    if (mastery >= 40) return 'text-amber-600';
    return 'text-rose-600';
  };
  const isAllFilteredSelected = filteredAndSortedStudents.length > 0 && filteredAndSortedStudents.every(s => selectedIds.has(s.id));
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set(selectedIds);
    if (e.target.checked) {
      filteredAndSortedStudents.forEach(s => newSelected.add(s.id));
    } else {
      filteredAndSortedStudents.forEach(s => newSelected.delete(s.id));
    }
    setSelectedIds(newSelected);
  };
  const handleExportCSV = () => {
    const selectedStudents = enrichedStudents.filter(s => selectedIds.has(s.id));
    if (selectedStudents.length === 0) return;
    const headers = ['Naam', 'Beheersing (%)', 'Oefendagen', 'Trend (%)', 'Status'];
    const rows = selectedStudents.map(s => [s.name, s.avgMastery.toString(), s.daysPracticed.toString(), s.trendDelta.toString(), s.needsAttention ? 'Aandacht nodig' : s.isGood ? 'Goed bezig' : s.isInactive ? 'Inactief' : 'Bezig']);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    downloadCsv(csvContent, 'klas_export');
  };
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return null;
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    let positiveTrend = 0;
    let negativeTrend = 0;
    enrichedStudents.forEach(s => {
      if (s.trendDelta > 0) positiveTrend++;else if (s.trendDelta < 0) negativeTrend++;
    });
    const attentionCount = enrichedStudents.filter(s => s.needsAttention).length;
    return {
      classAvg,
      positiveTrend,
      negativeTrend,
      attentionCount
    };
  }, [enrichedStudents]);
  const formatLastActive = (daysPracticed: number, weeklyActivity: DayActivity[]) => {
    if (daysPracticed === 0) return 'Nog niet';
    const recentDay = [...weeklyActivity].reverse().find(d => d.practiced);
    if (!recentDay) return 'Nog niet';
    return formatRelativeTime(new Date(recentDay.date).getTime());
  };
  return <PageLayout title="Mijn Klas" description={classroomName ? `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen in ${classroomName}` : `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen`} data-id="element-7144">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-7145">
        {classrooms.length === 0 && <Card className="p-8 border-indigo-100 shadow-sm text-center bg-white" data-id="element-7146">
            <div className="flex justify-center mb-4" data-id="element-7147">
              <DjayAvatar size="sm" emotion="thinking" data-id="element-7148" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-7149">
              Nog geen klas aangemaakt
            </h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-7150">
              Maak eerst een klas aan voordat je leerlingen kunt toevoegen. Dit
              helpt om je leerlingen overzichtelijk te houden.
            </p>
            <Button onClick={() => onNavigate?.('management')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl" data-id="element-7151">
              Naar Klas Beheer om klas te maken
            </Button>
          </Card>}

        {/* Summary Strip */}
        {classrooms.length > 0 && summaryStats && <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-7152">
            <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-7153">
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-7154">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-7155">
                  <CheckCircle2 className="w-5 h-5" data-id="element-7156" />
                </div>
                <div data-id="element-7157">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-7158">
                    Gem. beheersing
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-7159">
                    {summaryStats.classAvg}%
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-7160">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-7161">
                  <ArrowUpRight className="w-5 h-5" data-id="element-7162" />
                </div>
                <div data-id="element-7163">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-7164">
                    Trend
                  </div>
                  <div className="text-2xl font-black text-slate-900 flex items-baseline gap-2" data-id="element-7165">
                    <span className="text-emerald-600 flex items-center" data-id="element-7166">
                      {summaryStats.positiveTrend}{' '}
                      <ArrowUpRight className="w-4 h-4 ml-0.5" data-id="element-7167" />
                    </span>
                    <span className="text-rose-600 flex items-center text-lg" data-id="element-7168">
                      {summaryStats.negativeTrend}{' '}
                      <ArrowDownRight className="w-3 h-3 ml-0.5" data-id="element-7169" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-7170">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-7171">
                  <AlertTriangle className="w-5 h-5" data-id="element-7172" />
                </div>
                <div data-id="element-7173">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-7174">
                    Aandacht nodig
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-7175">
                    {summaryStats.attentionCount}
                  </div>
                </div>
              </div>
            </div>
          </Card>}

        {/* Controls */}
        {classrooms.length > 0 && <div className="flex flex-col gap-4" data-id="element-7176">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center" data-id="element-7177">
              <div className="flex flex-wrap items-center gap-2" data-id="element-7178">
                <FilterButton active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} label="Alle" count={counts.all} color="indigo" data-id="element-7179" />
                <FilterButton active={activeFilter === 'attention'} onClick={() => setActiveFilter('attention')} label="Aandacht" count={counts.attention} color="rose" data-id="element-7180" />
                <FilterButton active={activeFilter === 'good'} onClick={() => setActiveFilter('good')} label="Goed bezig" count={counts.good} color="emerald" data-id="element-7181" />
                <FilterButton active={activeFilter === 'inactive'} onClick={() => setActiveFilter('inactive')} label="Inactief" count={counts.inactive} color="amber" data-id="element-7182" />
              </div>

              <div className="flex gap-3 w-full sm:w-auto items-center" data-id="element-7183">
                <div className="relative flex-1 sm:w-48" data-id="element-7184">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-7185" />
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoeken..." className="pl-9 bg-white border-slate-200 rounded-xl w-full h-9 text-sm" data-id="element-7186" />
                </div>
                <Select value={activeSort} onChange={e => setActiveSort(e.target.value as SortType)} options={[{
              value: 'name',
              label: 'Naam'
            }, {
              value: 'mastery',
              label: 'Beheersing'
            }, {
              value: 'activity',
              label: 'Activiteit'
            }]} variant="admin" size="sm" fullWidth={false} className="bg-white border-slate-200 text-slate-700 font-medium rounded-xl h-9" data-id="element-7187" />
              </div>
            </div>
          </div>}

        {/* Compact Student Table */}
        {classrooms.length > 0 && (filteredAndSortedStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white relative" data-id="element-7188">
              {/* Table header */}
              <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7189">
                <div className="w-6 flex items-center justify-center" data-id="element-7190">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={isAllFilteredSelected} onChange={handleSelectAll} data-id="element-7191" />
                </div>
                <div className="w-8" data-id="element-7192"></div>
                <div data-id="element-7193">Naam</div>
                <div className="hidden md:block w-32" data-id="element-7194">Zwakste vak</div>
                <div className="hidden md:block w-28" data-id="element-7195">Laatst actief</div>
                <div className="text-right w-24" data-id="element-7196">Beheersing</div>
                <div className="w-6" data-id="element-7197"></div>
              </div>

              {/* Student rows */}
              <div className="divide-y divide-slate-100" data-id="element-7198">
                {filteredAndSortedStudents.map((student, i) => {
            const statusColor = student.needsAttention ? 'bg-rose-500' : student.isGood ? 'bg-emerald-500' : student.isInactive ? 'bg-amber-500' : 'bg-indigo-400';
            const statusLabel = student.needsAttention ? 'Aandacht nodig' : student.isGood ? 'Goed bezig' : student.isInactive ? 'Inactief' : 'Bezig';
            return <div key={student.id} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-7199">
                      {/* Checkbox */}
                      <div className="w-6 flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-7200">
                        <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={selectedIds.has(student.id)} onChange={e => {
                  const newSelected = new Set(selectedIds);
                  if (e.target.checked) {
                    newSelected.add(student.id);
                  } else {
                    newSelected.delete(student.id);
                  }
                  setSelectedIds(newSelected);
                }} data-id="element-7201" />
                      </div>

                      {/* Avatar */}
                      <div className="relative" data-id="element-7202">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm`} data-id="element-7203">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusColor}`} role="img" aria-label={statusLabel} title={statusLabel} data-id="element-7204" />
                      </div>

                      {/* Name */}
                      <div className="min-w-0" data-id="element-7205">
                        <div className="font-bold text-sm text-slate-900 truncate" data-id="element-7206">
                          {student.name}
                        </div>
                      </div>

                      {/* Weakest Subject */}
                      <div className="hidden md:block w-32" data-id="element-7207">
                        {student.weakestModule ? (() => {
                  const IconComp = getModuleIcon(student.weakestModule.icon);
                  return <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-medium text-slate-600 max-w-full hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors cursor-pointer" onClick={e => {
                    e.stopPropagation();
                    onSelectStudent(student.id);
                  }} data-id="element-7208">
                                <IconComp className="w-3 h-3 flex-shrink-0" data-id="element-7209" />
                                <span className="truncate" data-id="element-7210">
                                  {student.weakestModule.title}
                                </span>
                              </div>;
                })() : <span className="text-slate-400 text-sm" data-id="element-7211">—</span>}
                      </div>

                      {/* Last Active */}
                      <div className="hidden md:block w-28 text-sm text-slate-500 font-medium" data-id="element-7212">
                        {formatLastActive(student.daysPracticed, student.weeklyActivity)}
                      </div>

                      {/* Mastery */}
                      <div className="flex items-center justify-end gap-1.5 w-24" data-id="element-7213">
                        <span className={`font-black text-sm ${getMasteryColorClass(student.avgMastery)}`} data-id="element-7214">
                          {student.avgMastery}%
                        </span>
                        {student.trendDelta !== 0 && <span className={student.trendDelta > 0 ? 'text-emerald-500' : 'text-rose-500'} title={`Trend: ${student.trendDelta > 0 ? '+' : ''}${student.trendDelta}%`} data-id="element-7215">
                            {student.trendDelta > 0 ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-7216" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-7217" />}
                          </span>}
                      </div>

                      {/* Arrow */}
                      <div className="text-slate-300 w-6 flex justify-end" data-id="element-7218">
                        <ArrowRight className="w-4 h-4" data-id="element-7219" />
                      </div>
                    </div>;
          })}
              </div>
            </Card> : <EmptyState title="Geen leerlingen gevonden" message="Probeer je zoekopdracht of filters aan te passen." emotion="thinking" colorScheme="slate" data-id="element-7220" />)}
      </div>

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-10 fade-in duration-300" data-id="element-7221">
          <div className="font-bold" data-id="element-7222">
            {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'leerling' : 'leerlingen'} geselecteerd
          </div>
          <div className="w-px h-6 bg-slate-700" data-id="element-7223"></div>
          <div className="flex items-center gap-3" data-id="element-7224">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-id="element-7225">
              Deselecteer alles
            </button>
            <Button onClick={() => onNavigate?.('reports')} size="sm" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white border-slate-700" data-id="element-7226">
              <FileText className="w-4 h-4 mr-2" data-id="element-7227" />
              Bekijk Rapport
            </Button>
            <Button onClick={handleExportCSV} size="sm" className="bg-indigo-500 hover:bg-indigo-400 text-white border-none shadow-none" data-id="element-7228">
              <Download className="w-4 h-4 mr-2" data-id="element-7229" />
              Export CSV
            </Button>
          </div>
        </div>}
    </PageLayout>;
}
function FilterButton({
  active,
  onClick,
  label,
  count,
  color = 'indigo'
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  color?: 'indigo' | 'rose' | 'emerald' | 'amber';
}) {
  const colorClasses = {
    indigo: active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    rose: active ? 'bg-rose-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    emerald: active ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    amber: active ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
  };
  const badgeClasses = {
    indigo: active ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500',
    rose: active ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500',
    emerald: active ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500',
    amber: active ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-500'
  };
  return <button onClick={onClick} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${colorClasses[color]}`} data-id="element-7230">
      {label}
      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black leading-none ${badgeClasses[color]}`} data-id="element-7231">
        {count}
      </span>
    </button>;
}